import api from '@dashboard/services/axiosApi'
import type { HostedChange } from '@shared/configChanges'
import type { MovePosition } from '@shared/editor/protocol'
import type { PageQuestionSet } from './pageQuestions'

export type { HostedChange } from '@shared/configChanges'

export type TeamPid = number | string

export type HostedPageStatus = 'live' | 'changed' | 'draft' | 'offline'

export interface HostedPageSummary {
  id: string
  name: string
  path: string
  status: HostedPageStatus
  liveUrl: string | null
  headRevisionId: number
  updatedAt: string
}

export interface HostedSite {
  servingHost: string | null
  slug: string | null
  pages: HostedPageSummary[]
  changes: HostedChange[]
  digest: string
  /** Why the next Publish would leave every hosted change out. */
  block: { reason: string } | null
}

export interface HostedPageSettings {
  title: string | null
  description: string | null
  socialImage: string | null
  favicon: string | null
  canonical: string | null
  noindex: boolean
  lang: string | null
}

export interface HostedScripts {
  head: string
  bodyEnd: string
}

export interface HostedPage extends HostedPageSummary {
  /** The page's draft address on the serving host; campaign page rules on this page name it. */
  pageUrl: string
  /** Every hosted host the project owns, serving first: rules written before a host change still name the old one. */
  hosts: string[]
  /** The path the current release serves this page at; null before the first publish. */
  publishedPath: string | null
  settings: HostedPageSettings
  /** False when the page has its own look instead of the shared site styles. */
  usesSiteStyles: boolean
  scripts: HostedScripts
  /** The editor canvas: a static shell on the project's hosted origin. */
  editor: { shellUrl: string }
  /** The build the page was created with while no Righty turn has taken it; the editor offers to start it. */
  unstartedBuildId: number | null
}

export type RevisionSource =
  | 'create'
  | 'copy'
  | 'visual'
  | 'code'
  | 'righty'
  | 'mcp'
  | 'undo'
  | 'redo'
  | 'restore'
  | 'capture'

/** One immutable page revision: the storage form for the Code tab and the canvas form for the shell. */
export interface PageRevisionPayload {
  revision: { id: number; number: number; parentId: number | null; source: RevisionSource }
  html: string
  css: string
  canvas: { body: string; css: string }
  settings: HostedPageSettings
  /** The page's own look, shaped like the site styles' canvas and fields; null when the page uses the site styles. */
  look: PageLook | null
  slots: { widgetId: string; area: string }[]
  hidden: { id: string; label: string }[]
  placeholders: { id: string; kind: string }[]
  canUndo: boolean
  canRedo: boolean
}

export interface PageLook {
  /** The page's full stylesheet: tokens as :root variables, base CSS and the widgets CSS. */
  css: string
  fontsHref: string | null
  fields: SiteStyleField[]
  /** The page's dark palette (shown to visitors whose device is set to dark); null when it has none. */
  dark: SiteStyleField[] | null
}

export interface RevisionSummary {
  id: number
  number: number
  parentId: number | null
  restoredRevisionId: number | null
  source: RevisionSource
  summary: string | null
  userId: number | null
  createdAt: string
}

export type PageEditInput =
  | { op: 'set_text' | 'replace'; elementId: string; seen: number; html: string; css?: string }
  | { op: 'set_image'; elementId: string; seen: number; src: string; alt: string }
  | { op: 'set_css'; elementId: string; seen: number; css: string }
  | { op: 'hide' | 'show' | 'remove' | 'duplicate'; elementId: string; seen: number }
  | { op: 'move'; elementId: string; seen: number; targetId: string; position: MovePosition }
  | { op: 'document'; html: string; css: string; base: number }
  | { op: 'settings'; settings: Partial<HostedPageSettings> }
  | { op: 'look'; useSiteStyles: boolean }
  | { op: 'look'; tokens: StyleTokenEdit }

/**
 * A token edit, merged per key: light tokens by key, and `dark` for the dark palette, merged per color (`{}` adds the
 * palette derived from the light colors, null removes it).
 */
export type StyleTokenEdit = Record<string, string | Record<string, string> | null>

export interface SiteStyleField {
  key: string
  label: string
  type: 'color' | 'font' | 'length' | 'image'
  /** The CSS custom property a live preview overrides; null for fields that aren't variables (logo, favicon). */
  variable: string | null
  value: string | null
}

export interface SiteStylePayload {
  revision: { id: number; number: number; parentId: number | null; source: RevisionSource }
  css: string
  canvas: {
    css: string
    fontsHref: string | null
    /** The CSS font-family pages render each set font token in (an alias as the Google family it names), by its value. */
    fonts: { value: string; family: string }[]
  }
  fields: SiteStyleField[]
  /** The site's dark palette (shown to visitors whose device is set to dark); null when it has none. */
  dark: SiteStyleField[] | null
  capture: {
    status: 'captured' | 'failed' | 'none' | 'capturing'
    website: string | null
    error: string | null
    capturedAt: string | null
    substitutions: { key: string; requested: string; used: string }[]
  }
  canUndo: boolean
  canRedo: boolean
}

export type PageGoal =
  | { kind: 'form'; contentId?: string }
  | { kind: 'quiz' | 'survey'; contentId?: string }
  | { kind: 'url'; url: string }

export interface ReferenceShot {
  id: string
  url: string
  imageUrl: string
  ownDomain: boolean
}

/** How a brief build starts: the analysis reads it to ask its questions, and `POST hosted/pages` builds from it. */
export interface PageStart {
  brief: string
  goal: PageGoal
  references: string[]
  useSiteStyles: boolean
}

export type CreatePageInput =
  /** `clarifications`: the answered question set, posted back whole; omitted when nothing was asked or it was skipped. */
  | { name: string; path: string; start: PageStart & { clarifications?: PageQuestionSet } }
  | { name: string; path: string; copyFrom: string }

export interface CreatedPage {
  page: HostedPage
  build: { id: number } | null
}

/** Null values mean reporting was unavailable for that range: show —, never 0. */
export type HostedPageMetrics = Record<string, { visitors: number | null; conversions: number | null }>

export type HostedPublishOutcome = {
  outcome: 'nothing_pending' | 'published' | 'blocked' | 'failed'
  reason?: string
  blockedPages: { pageId: string; reason: string }[]
}

/** A publish's hosted outcome with each blocked page named for the review. */
export type HostedPublishResult = Omit<HostedPublishOutcome, 'blockedPages'> & {
  blockedPages: { pageId: string; name: string; reason: string }[]
}

// Routes pass the project as a string and the config store as a number; one key keeps every reader on one cache entry.
export const hostedSiteQueryKey = (teamPid: TeamPid | null) =>
  ['hostedSite', teamPid === null ? null : String(teamPid)] as const

const base = (teamPid: TeamPid) => `/api/${teamPid}/hosted`

export const fetchHostedSite = (teamPid: TeamPid) =>
  api.get<HostedSite>(`${base(teamPid)}/site`).then((response) => response.data)

export const fetchHostedPage = (teamPid: TeamPid, pageId: string) =>
  api.get<HostedPage>(`${base(teamPid)}/pages/${pageId}`).then((response) => response.data)

export const updateHostedPage = (
  teamPid: TeamPid,
  pageId: string,
  changes: { name?: string; path?: string; held?: false }
) => api.patch<HostedPage>(`${base(teamPid)}/pages/${pageId}`, changes).then((response) => response.data)

/** Deletes the page; `deleteCampaign` (one of its `HostedPageCampaign`s) is deleted from the draft first. */
export const deleteHostedPage = (teamPid: TeamPid, pageId: string, deleteCampaign: string | null = null) =>
  api
    .delete(`${base(teamPid)}/pages/${pageId}`, deleteCampaign ? { data: { deleteCampaign } } : undefined)
    .then(() => undefined)

/**
 * A campaign tied to a page (`HostedCampaignRules::pageCampaigns`): with changes on it, based on it (the page
 * campaign), or, for a `path`, with rules that move with it.
 */
export interface HostedPageCampaign {
  id: string
  name: string
  active: boolean
  /** Changes on this page, in every audience and test version. */
  changes: number
  /** The newest campaign built on this page's address: where the page editor puts new audiences. */
  pageCampaign: boolean
  /** Every change of the campaign is on this page. */
  onlyThisPage: boolean
  runningTest: boolean
  /** False while one of its tests runs live: the test must end first. */
  deletable: boolean
  /** Moving to `path` rewrites some of its exact page rules, base_url or goal rules. */
  moved: boolean
  /** Moving to `path` leaves changes whose non-exact rules don't cover the new address. */
  uncovered: boolean
}

export const fetchPageCampaigns = (teamPid: TeamPid, pageId: string, path: string | null = null) =>
  api
    .get<{
      campaigns: HostedPageCampaign[]
    }>(`${base(teamPid)}/pages/${pageId}/campaigns`, path === null ? undefined : { params: { path } })
    .then((response) => response.data.campaigns)

export const unpublishHostedPage = (teamPid: TeamPid, pageId: string) =>
  api.post(`${base(teamPid)}/pages/${pageId}/unpublish`).then(() => undefined)

/** A fresh draft preview: the page's draft at its real address, served for an hour (`rm_draft` token). */
export const createPagePreview = (teamPid: TeamPid, pageId: string) =>
  api
    .post<{ url: string; expiresAt: string }>(`${base(teamPid)}/pages/${pageId}/preview`)
    .then((response) => response.data)

export const updateHostedPageSettings = (teamPid: TeamPid, pageId: string, settings: Partial<HostedPageSettings>) =>
  api.patch<HostedPage>(`${base(teamPid)}/pages/${pageId}/settings`, settings).then((response) => response.data)

export const saveHostedPageScripts = (teamPid: TeamPid, pageId: string, scripts: HostedScripts) =>
  api.put<HostedPage>(`${base(teamPid)}/pages/${pageId}/scripts`, scripts).then((response) => response.data)

export const fetchProjectScripts = (teamPid: TeamPid) =>
  api.get<HostedScripts>(`${base(teamPid)}/scripts`).then((response) => response.data)

export const saveProjectScripts = (teamPid: TeamPid, scripts: HostedScripts) =>
  api.put<HostedScripts>(`${base(teamPid)}/scripts`, scripts).then((response) => response.data)

export const saveHostedSlug = (teamPid: TeamPid, slug: string) =>
  api.put(`${base(teamPid)}/slug`, { slug }).then(() => undefined)

export type HostedDomainStatus = 'pending_dns' | 'pending_certificate' | 'active' | 'failed'

export interface HostedDomain {
  hostname: string
  status: HostedDomainStatus
  /** Why it isn't active yet, in words the customer can act on. */
  problem: { code: string; message: string } | null
  /** The record to add: `name` is relative to `zone` when we know the zone, else the full hostname. */
  record: { type: 'CNAME'; name: string; zone: string | null; value: string }
  checkedAt: string | null
  nextCheckAt: string | null
  activatedAt: string | null
}

export interface HostedDomains {
  /** False until Cloudflare for SaaS is configured for this environment. */
  available: boolean
  slug: string | null
  subdomainHost: string | null
  servingHost: string | null
  domain: HostedDomain | null
}

export const hostedDomainsQueryKey = (teamPid: TeamPid | null) => ['hostedDomains', teamPid] as const

export const fetchHostedDomains = (teamPid: TeamPid) =>
  api.get<HostedDomains>(`${base(teamPid)}/domains`).then((response) => response.data)

export const addHostedDomain = (teamPid: TeamPid, hostname: string) =>
  api.post<HostedDomains>(`${base(teamPid)}/domains`, { hostname }).then((response) => response.data)

export const refreshHostedDomain = (teamPid: TeamPid) =>
  api.post<HostedDomains>(`${base(teamPid)}/domains/refresh`).then((response) => response.data)

export const removeHostedDomain = (teamPid: TeamPid) =>
  api.delete<HostedDomains>(`${base(teamPid)}/domains`).then((response) => response.data)

export const fetchHostedPageMetrics = (teamPid: TeamPid, params: { from: string; to: string; timezone: string }) =>
  api.get<HostedPageMetrics>(`${base(teamPid)}/pages/metrics`, { params }).then((response) => response.data)

export const fetchPageRevision = (teamPid: TeamPid, pageId: string, revision: number | 'head') =>
  api
    .get<PageRevisionPayload>(`${base(teamPid)}/pages/${pageId}/revisions/${revision}`)
    .then((response) => response.data)

export const fetchPageHistory = (teamPid: TeamPid, pageId: string, before?: number) =>
  api
    .get<{ revisions: RevisionSummary[]; cursor: { canUndo: boolean; canRedo: boolean } }>(
      `${base(teamPid)}/pages/${pageId}/revisions`,
      { params: before === undefined ? {} : { before } }
    )
    .then((response) => response.data)

export const applyPageEdit = (teamPid: TeamPid, pageId: string, edit: PageEditInput) =>
  api.post<PageRevisionPayload>(`${base(teamPid)}/pages/${pageId}/edits`, edit).then((response) => response.data)

export const undoPage = (teamPid: TeamPid, pageId: string, expectHead: number) =>
  api
    .post<PageRevisionPayload>(`${base(teamPid)}/pages/${pageId}/undo`, { expectHead })
    .then((response) => response.data)

export const redoPage = (teamPid: TeamPid, pageId: string, expectHead: number) =>
  api
    .post<PageRevisionPayload>(`${base(teamPid)}/pages/${pageId}/redo`, { expectHead })
    .then((response) => response.data)

export const restorePage = (teamPid: TeamPid, pageId: string, revisionId: number, expectHead: number) =>
  api
    .post<PageRevisionPayload>(`${base(teamPid)}/pages/${pageId}/restore`, { revisionId, expectHead })
    .then((response) => response.data)

export const fetchSiteStyles = (teamPid: TeamPid) =>
  api.get<SiteStylePayload>(`${base(teamPid)}/site-styles`).then((response) => response.data)

export const editSiteStyles = (teamPid: TeamPid, edit: { tokens: StyleTokenEdit } | { css: string; base: number }) =>
  api.post<SiteStylePayload>(`${base(teamPid)}/site-styles/edits`, edit).then((response) => response.data)

export const undoSiteStyles = (teamPid: TeamPid, expectHead: number) =>
  api.post<SiteStylePayload>(`${base(teamPid)}/site-styles/undo`, { expectHead }).then((response) => response.data)

export const redoSiteStyles = (teamPid: TeamPid, expectHead: number) =>
  api.post<SiteStylePayload>(`${base(teamPid)}/site-styles/redo`, { expectHead }).then((response) => response.data)

export const recaptureSiteStyles = (teamPid: TeamPid) =>
  api.post<SiteStylePayload>(`${base(teamPid)}/site-styles/recapture`).then((response) => response.data)

export const captureReference = (teamPid: TeamPid, url: string, signal?: AbortSignal) =>
  api.post<ReferenceShot>(`${base(teamPid)}/references`, { url }, { signal }).then((response) => response.data)

export const suggestPageName = (teamPid: TeamPid, input: { brief: string; goal: PageGoal }, signal?: AbortSignal) =>
  api
    .post<{ name: string; path: string }>(`${base(teamPid)}/pages/suggest`, input, { signal })
    .then((response) => response.data)

/** Righty's questions about a brief start; the response is parsed by `readQuestionSet`. Writes nothing. */
export const askPageQuestions = (teamPid: TeamPid, input: { name?: string; start: PageStart }, signal?: AbortSignal) =>
  api.post<unknown>(`${base(teamPid)}/pages/questions`, input, { signal }).then((response) => response.data)

export const createHostedPage = (teamPid: TeamPid, input: CreatePageInput) =>
  api.post<CreatedPage>(`${base(teamPid)}/pages`, input).then((response) => response.data)
